module.exports = {
    slug: 'exemple-facture-factur-x-xml',
    title: 'Exemple de facture Factur-X : le XML commenté pas à pas',
    h1: 'Exemple de facture Factur-X : le fichier XML commenté pas à pas',
    description: 'Exemple de facture Factur-X : le PDF, le XML embarqué, les champs clés (type 380, SIREN, TVA, acompte) et comment vérifier un fichier avant l\'envoi.',
    category: 'Formats',
    published: '2026-11-04',
    updated: '2026-11-04',
    lead: 'Une facture Factur-X ressemble à un PDF ordinaire, mais elle cache un fichier **XML** que le logiciel de votre client lit tout seul. Voici à quoi ressemble ce fichier, champ par champ, sur l\'exemple d\'une facture finale avec acompte.',
    sections: [
        {
            id: 'contenu',
            h2: 'Ce que contient un fichier Factur-X',
            blocks: [
                {p: 'Un fichier Factur-X est un **PDF/A-3** : la facture que votre client voit, plus une pièce jointe nommée **factur-x.xml**, qui contient les mêmes informations découpées en champs. Le PDF et le XML doivent dire la même chose : un écart entre les deux est une anomalie.'},
                {p: 'Pour les principes (profils, formats, obligations), voir [Factur-X : le format expliqué](/blog/factur-x-format-facture-electronique). Cette page montre à quoi ressemble le XML.'}
            ]
        },
        {
            id: 'xml',
            h2: 'Un exemple de XML (profil MINIMUM)',
            blocks: [
                {p: 'Voici une facture finale fictive : une prestation de 950,00 € HT, TVA à 20 %, soit 1 140,00 € TTC, dont 342,00 € déjà versés en acompte. C\'est l\'exemple du guide sur la [facture d\'acompte et la facture finale](/blog/facture-acompte-facture-finale). Pour rester lisible, l\'extrait suit le profil **MINIMUM**, le plus court.'},
                {code: [
                    '<rsm:CrossIndustryInvoice xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"',
                    '    xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"',
                    '    xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100">',
                    '  <rsm:ExchangedDocumentContext>',
                    '    <ram:GuidelineSpecifiedDocumentContextParameter>',
                    '      <ram:ID>urn:factur-x.eu:1p0:minimum</ram:ID>',
                    '    </ram:GuidelineSpecifiedDocumentContextParameter>',
                    '  </rsm:ExchangedDocumentContext>',
                    '  <rsm:ExchangedDocument>',
                    '    <ram:ID>F0042</ram:ID>',
                    '    <ram:TypeCode>380</ram:TypeCode>',
                    '    <ram:IssueDateTime>',
                    '      <udt:DateTimeString format="102">20261104</udt:DateTimeString>',
                    '    </ram:IssueDateTime>',
                    '  </rsm:ExchangedDocument>',
                    '  <rsm:SupplyChainTradeTransaction>',
                    '    <ram:ApplicableHeaderTradeAgreement>',
                    '      <ram:SellerTradeParty>',
                    '        <ram:Name>Atelier Exemple</ram:Name>',
                    '        <ram:SpecifiedLegalOrganization>',
                    '          <ram:ID schemeID="0002">123456782</ram:ID>',
                    '        </ram:SpecifiedLegalOrganization>',
                    '        <ram:PostalTradeAddress>',
                    '          <ram:CountryID>FR</ram:CountryID>',
                    '        </ram:PostalTradeAddress>',
                    '        <ram:SpecifiedTaxRegistration>',
                    '          <ram:ID schemeID="VA">FR11123456782</ram:ID>',
                    '        </ram:SpecifiedTaxRegistration>',
                    '      </ram:SellerTradeParty>',
                    '      <ram:BuyerTradeParty>',
                    '        <ram:Name>Client Exemple SAS</ram:Name>',
                    '        <ram:SpecifiedLegalOrganization>',
                    '          <ram:ID schemeID="0002">987654324</ram:ID>',
                    '        </ram:SpecifiedLegalOrganization>',
                    '      </ram:BuyerTradeParty>',
                    '    </ram:ApplicableHeaderTradeAgreement>',
                    '    <ram:ApplicableHeaderTradeDelivery/>',
                    '    <ram:ApplicableHeaderTradeSettlement>',
                    '      <ram:InvoiceCurrencyCode>EUR</ram:InvoiceCurrencyCode>',
                    '      <ram:SpecifiedTradeSettlementHeaderMonetarySummation>',
                    '        <ram:TaxBasisTotalAmount>950.00</ram:TaxBasisTotalAmount>',
                    '        <ram:TaxTotalAmount currencyID="EUR">190.00</ram:TaxTotalAmount>',
                    '        <ram:GrandTotalAmount>1140.00</ram:GrandTotalAmount>',
                    '        <ram:TotalPrepaidAmount>342.00</ram:TotalPrepaidAmount>',
                    '        <ram:DuePayableAmount>798.00</ram:DuePayableAmount>',
                    '      </ram:SpecifiedTradeSettlementHeaderMonetarySummation>',
                    '    </ram:ApplicableHeaderTradeSettlement>',
                    '  </rsm:SupplyChainTradeTransaction>',
                    '</rsm:CrossIndustryInvoice>'
                ].join('\n')},
                {note: 'Exemple fictif et simplifié, à titre d\'illustration : les noms, SIREN et numéro de TVA sont inventés. Le profil MINIMUM n\'est pas conforme à la norme EN 16931 ; un logiciel de facturation vise généralement le profil EN 16931, plus complet (lignes, TVA par taux, conditions de paiement).'}
            ]
        },
        {
            id: 'champs',
            h2: 'Les champs à connaître',
            blocks: [
                {table: {
                    head: ['Balise', 'Ce qu\'elle contient'],
                    rows: [
                        ['GuidelineSpecifiedDocumentContextParameter / ID', 'Le profil Factur-X du fichier (ici MINIMUM)'],
                        ['ExchangedDocument / ID', 'Le numéro de la facture'],
                        ['ExchangedDocument / TypeCode', 'Le type de document : 380 pour une facture, 381 pour un avoir (note de crédit)'],
                        ['IssueDateTime', 'La date d\'émission ; format 102 signifie AAAAMMJJ'],
                        ['SellerTradeParty', 'Le vendeur : nom, SIREN (schemeID 0002), pays et numéro de TVA'],
                        ['BuyerTradeParty', 'L\'acheteur : nom et SIREN'],
                        ['InvoiceCurrencyCode', 'La devise de la facture'],
                        ['TaxBasisTotalAmount', 'Le total hors taxes'],
                        ['TaxTotalAmount', 'Le total de TVA'],
                        ['GrandTotalAmount', 'Le total TTC'],
                        ['TotalPrepaidAmount', 'Le montant déjà payé, par exemple un acompte'],
                        ['DuePayableAmount', 'Le net à payer : total TTC moins le montant déjà payé']
                    ]
                }}
            ]
        },
        {
            id: 'acompte-avoir',
            h2: 'L\'acompte et l\'avoir dans le XML',
            blocks: [
                {p: 'Sur la facture finale, les lignes gardent leur montant réel. L\'acompte n\'apparaît pas comme une ligne négative : la règle **BR-27** de la norme EN 16931 interdit un prix net d\'article négatif. Il est déclaré dans les totaux, par **TotalPrepaidAmount**, ce qui ramène le net à payer au solde de 798,00 €.'},
                {p: 'Une **facture d\'acompte** est une facture comme les autres, et peut porter un type de document propre (386, facture d\'acompte). Un **avoir** garde la même structure, avec le type **381**. Voir [facture d\'avoir](/blog/facture-avoir-comment-etablir).'}
            ]
        },
        {
            id: 'verifier',
            h2: 'Comment vérifier un fichier Factur-X',
            blocks: [
                {p: 'Avant de généraliser l\'envoi, testez quelques factures :'},
                {ul: [
                    'ouvrez le PDF dans un lecteur qui affiche les **pièces jointes** et vérifiez la présence de factur-x.xml ;',
                    'comparez **les totaux du XML et du PDF** : HT, TVA, TTC et net à payer ;',
                    'passez le fichier dans un **validateur Factur-X**, qui contrôle le profil et les règles de la norme ;',
                    'faites un essai de transmission avec votre plateforme agréée.'
                ]},
                {p: 'Vous n\'avez pas à écrire ce XML vous-même : votre logiciel le produit. Le point à vérifier est qu\'il gère bien les acomptes et les avoirs. Voir la page [facturation électronique](/gestionnaire-citron/facturation-electronique).'}
            ]
        }
    ],
    faq: [
        {q: 'Comment s\'appelle le fichier XML d\'une facture Factur-X ?', a: 'Il s\'appelle factur-x.xml et il est embarqué dans le PDF/A-3. Un lecteur PDF qui affiche les pièces jointes permet de le retrouver.'},
        {q: 'Quel profil Factur-X choisir ?', a: 'Les logiciels de facturation visent généralement le profil EN 16931, qui reprend l\'ensemble des informations de la norme européenne. Le profil MINIMUM est plus succinct. Voir [les profils Factur-X](/blog/factur-x-format-facture-electronique).'},
        {q: 'Le XML doit-il être identique au PDF ?', a: 'Les montants et les mentions doivent concorder. Un écart entre le PDF et le XML est une anomalie.'},
        {q: 'Peut-on modifier un fichier Factur-X après l\'avoir créé ?', a: 'Non : une facture émise ne se retouche pas. On la corrige par un avoir ou une facture rectificative.'}
    ],
    sources: [
        {label: 'FNFE-MPE : présentation du standard Factur-X', url: 'https://fnfe-mpe.org/factur-x/factur-x_en/'},
        {label: 'economie.gouv.fr : tout savoir sur la facturation électronique', url: 'https://www.economie.gouv.fr/tout-savoir-sur-la-facturation-electronique-pour-les-entreprises'},
        {label: 'impots.gouv.fr : spécifications externes et normes de la facturation électronique (formats et profils)', url: 'https://www.impots.gouv.fr/factures-norme-afnor'},
        {label: 'FNFE-MPE : page Factur-X en français (spécification à télécharger)', url: 'https://fnfe-mpe.org/factur-x/'}
    ],
    related: ['factur-x-format-facture-electronique', 'facture-acompte-facture-finale', 'facture-avoir-comment-etablir']
};
